import { AutocompleteAPI, DynamicFieldsAPI, FetchAPI, FieldAPI, FieldsetAPI, FormAPI, InputAPI, LocatorAPI, Metadata, ToastService } from '@philips-internal/oswald-ui-components';
import Controller, { OnBeforeSubmit, OnBlur, OnCancel, OnChange, OnMount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableSubmitEvent, ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { DevicesCreateAliases as Aliases, FragmentNames } from '../../../codegen';
import { Endpoint } from '../../../constants/endpoints';
import BaseController from '../../BaseController';
import { LocatorTypes } from '../../locator/locator-controller';
import { Inject} from '@odinjs/odin';
import moment from 'moment';
import { Value } from '@philips-internal/oswald-ui-core';
import { ISO_FULL_DATE_FORMAT } from '../../../constants/date-utils';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { HealthProfessionalUser } from '../../../app/authentication';
import timeService from '../../../services/time-service';
import { Validator } from '@philips-internal/oswald-ui-core/lib/services/model-service';
import { SimpleDialog } from '../../dialog-helper';
import { DialogType, ModalType, SimpleDialogParams } from '../../../interfaces/simple-dialog';
import { DevicesDynamicForm } from '../commons/devices-dynamic-form';

interface TopographyData {
  topographyDescription: string;
  topographySequence: number;
}

interface GenericAutocompleteData {
  description: string;
  id: number;
}

interface DeviceDataSelected {
  description: string;
  id: number;
}

type ComplementDeviceData = GenericAutocompleteData;
type DeviceData = GenericAutocompleteData;
type InstallationData = GenericAutocompleteData;
interface ShowSideResponse {
  showSide: string;
}

interface CaliberData {
  caliberDescription: string;
  caliberSequence: number;
}

interface ProfessionalData {
  personCode: number;
  personName: string;
}

interface SideData {
  sideDeviceCode: string;
  sideDeviceDescription: string;
}

interface ActionData {
  internalProcedureDescription: string;
  internalProcedureSequence: number;
}

interface FormData {
  action: ActionData;
  additionalBehavior: ComplementDeviceData;
  attempts: number;
  bronchoaspiration: boolean;
  caliber: CaliberData;
  device: DeviceData;
  deviceUsed: number;
  dressingScheduleRemoval: string;
  enteralNoseProbe: boolean;
  installationSuccess: InstallationData;
  notes: string;
  permanence: number;
  person: ProfessionalData;
  plannedPermanence: number;
  scheduleRemoval: string;
  setupDate: string;
  side: SideData;
  topography: TopographyData;
  unconscious: boolean;
}

interface DeviceRegisterResponse {
  attendanceDeviceSequence: number;
  eventSequence: number;
  reasonExpression: number;
}

interface DeviceAttributesFocus {
  bandageDevice?: string;
  hoursForecastCuretive?: number;
  hoursForecastDevice?: number;
  serviceSector?: number;
  stay?: string;
  topography?: string;
}

const EXPRESSION_NOT_BE_INSTALL = 503342;
const EXPRESSION_WANT_CONTINUE = 503343;

@Controller({ fragment: FragmentNames.DEVICES_CREATE })
export class DevicesCreateController extends BaseController {

  @Inject(Aliases.FIELD_TOPOGRAPHY)
  topoGraphyFieldApi!: FieldAPI;

  @Inject(Aliases.INPUT_SETUP_DATE)
  setupDateInputApi!: InputAPI;

  @Inject(Aliases.AUTOCOMPLETE_SIDE)
  sideInputApi!: AutocompleteAPI;

  @Inject(Aliases.FORM_DEVICES)
  devicesFormApi!: FormAPI;

  @Inject(Aliases.AUTOCOMPLETE_CALIBER)
  caliberAutocompleteApi!: AutocompleteAPI;

  @Inject(Aliases.AUTOCOMPLETE_TOPOGRAPHY)
  topographyAutocompleteApi!: AutocompleteAPI;

  @Inject(Aliases.AUTOCOMPLETE_ACTION)
  actionAutocompleteApi!: AutocompleteAPI;

  @Inject(Aliases.INPUT_SCHEDULE_RENOVAL)
  scheduleRemoveInputApi!: InputAPI;

  @Inject(Aliases.INPUT_DRESSING_SCHEDULE_REMOVAL)
  dressignScheduleRemovalInputApi!: FieldAPI;

  @Inject(Aliases.FETCH_CALIBER)
  caliberFetchApi!: FetchAPI;

  @Inject(Aliases.AUTOCOMPLETE_ADDITIONAL_BEHAVIOR)
  additionalAutocompleteApi!: AutocompleteAPI;

  @Inject(Aliases.FIELD_INSTALATION_SUCCESS)
  instalationSuccessFieldApi!: FieldAPI;

  @Inject(Aliases.FIELD_PROFESSIONAL)
  professionalFieldApi!: FieldAPI;

  @Inject(Aliases.FIELDSET_DYNAMIC_FIELDS)
  fieldsetDynamicAPI!: FieldsetAPI;

  @Inject(Aliases.DYNAMIC_FIELDS)
  dynamicFieldAPI!: DynamicFieldsAPI;

  @Inject(Aliases.FIELD_PERMANENCE)
  permanenceFieldAPI!: FieldAPI;

  @Inject(Aliases.FIELD_SCHEDULE_REMOVAL)
  scheduleRemovalFieldAPI!: FieldAPI;

  @OnMount(Aliases.FORM_DEVICES)
  handleFormMount(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, locator: LocatorTypes.PHYSICAL_PERSON, patientId });
    timeService.getCurrentDateTime()
      .then(date => this.setupDateInputApi.setValue(date))
      .catch(() => this.setupDateInputApi.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
  }

  @OnMount(Aliases.DYNAMIC_FIELDS)
  handleMountDynamicFields(): void {
    const dynamicFields = this.context.get('dynamicFields') as Metadata;
    this.dynamicFieldAPI.setMetadata(dynamicFields);
  }

  @OnMount(Aliases.AUTOCOMPLETE_INSTALATION_SUCCESS)
  handleMountInstallationSuccess(event: MountEvent): void {
    const fieldApi = event.target as AutocompleteAPI;
    const data = [
      {
        description: MessageService.translate('label.status-yes'),
        id: 'S',
      },
      {
        description: MessageService.translate('label.status-no'),
        id: 'N',
      }
    ]
    fieldApi.setData(data);
    fieldApi.setValue(data[0]);
  }

  @OnMount(Aliases.LOCATOR_PROFESSIONAL)
  hadleMountProfessionalLocatorField(event: MountEvent): void {
    const locatorApi = event.target as LocatorAPI;
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;

    user && locatorApi.setValue({
      personCode: user.physicalPersonId,
      personName: user.name,
    });
  }

  @OnValidate(Aliases.FORM_DEVICES)
  handleValidateForm(): void {
    this.validateAllFields();
  }

  @OnCancel(Aliases.FORM_DEVICES)
  handleCancelForm(): void {
    this.historyService.back();
  }

  @OnBlur(Aliases.INPUT_PERMANENCE)
  handleChangePermanenceField(): void {
    const { setupDate, permanence } = this.devicesFormApi.getRecord() as unknown as FormData;

    if (setupDate && permanence) {
      const setupDateObj = moment(setupDate);
      setupDateObj.add(permanence, 'hours');
      this.scheduleRemoveInputApi.setValue(setupDateObj.utc().format(ISO_FULL_DATE_FORMAT));
    }
  }

  @OnBlur(Aliases.INPUT_PLANNED_PERMANENCE)
  handleChangePlannedPermanenceField(): void {
    const { setupDate, plannedPermanence } = this.devicesFormApi.getRecord() as unknown as FormData;

    if (setupDate && plannedPermanence) {
      const setupDateObj = moment(setupDate);
      setupDateObj.add(plannedPermanence, 'hours');
      this.dressignScheduleRemovalInputApi.setValue(setupDateObj.utc().format(ISO_FULL_DATE_FORMAT));
    }
  }

  @OnBeforeSubmit(Aliases.FORM_DEVICES)
  handleBeforeSubmitForm(event: AbortableSubmitEvent): void {
    const formInfos = DevicesDynamicForm.createFormPattern(event);
    const {
      bronchoaspiration,
      additionalBehavior,
      device,
      topography,
      person,
      attempts,
      caliber,
      installationSuccess,
      plannedPermanence,
      permanence,
      side,
      dressingScheduleRemoval,
      scheduleRemoval,
      enteralNoseProbe,
      action,
      deviceUsed,
      setupDate,
      unconscious,
      notes
    } = this.devicesFormApi.getRecord() as unknown as FormData;
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const body = {
      actionDeviceSequence: action && action.internalProcedureSequence,
      additionalInformationSequence: additionalBehavior && additionalBehavior.id,
      attemptsQuantity: attempts,
      bronchoaspirationStatus: bronchoaspiration ? 'S' : 'N',
      caliberSequence: caliber && caliber.caliberSequence,
      deviceAttributes: formInfos,
      deviceSequence: device.id,
      devicesQuantity: deviceUsed,
      dressingHoursQuantity: plannedPermanence,
      encounterId: Number(encounterId),
      ensStatus: enteralNoseProbe ? 'S' : 'N',
      observationDescription: notes,
      optionStatus: 'N',
      professionalCode: person.personCode,
      registerDate: moment(setupDate).utc().format(ISO_FULL_DATE_FORMAT),
      removedDateDressing: dressingScheduleRemoval && moment(dressingScheduleRemoval).utc().format(ISO_FULL_DATE_FORMAT),
      removedDatePlanned: scheduleRemoval && moment(scheduleRemoval).utc().format(ISO_FULL_DATE_FORMAT),
      sideStatus: side && side.sideDeviceCode,
      stayHour: permanence,
      successStatus: String(installationSuccess.id),
      topographySequence: topography && topography.topographySequence,
      unconsciousStatus: unconscious ? 'S' : 'N',
    };
    this.submitCreateDevice(body);
  }

  private RequestAttributes(deviceSequence: number): void {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const params = {
      attendance: encounterId,
      seqDevice: deviceSequence,
    }
    HttpService.get(Endpoint.GET_DEVICE_ATTRIBUTES, params)
      .then(response => {
        const resBody = response.body as DeviceAttributesFocus;
        const validator: Validator = { name: 'required', options: {}};
        if (resBody.stay === 'D') {
          this.permanenceFieldAPI.addValidator(validator);
          this.scheduleRemovalFieldAPI.addValidator(validator);
          return;
        }
        this.permanenceFieldAPI.removeValidator('required');
        this.scheduleRemovalFieldAPI.removeValidator('required');
      })
  }

  @OnChange(Aliases.FIELD_DEVICES)
  handleDevicesChange(event: ChangeEvent<FieldAPI>): void {
    const resValue = event.value as unknown as DeviceDataSelected;
    if (event && resValue) {
      this.handleDynamicFields(resValue.id);
      this.RequestAttributes(resValue.id);
    }
  }

  private submitCreateDevice(body: Record<string, Value>): void {
    this.toggleLoading()
    HttpService.post<DeviceRegisterResponse>(Endpoint.POST_VERIFY_DEVICES, undefined, { body })
      .then(response => response.body && this.handlerRegisterDevice(response.body, body))
      .catch(error => {
        this.toggleLoading();
        this.showToast(error?.body.errorMessage, ToastLevel.ERROR)
      })
  }

  private handleDynamicFields(deviceSequence: number): void {
    const deviceType = 'I';

    DevicesDynamicForm.handleDynamicFields(this.fieldsetDynamicAPI, deviceSequence, deviceType, this.context);
  }

  private registerDevice(body: Record<string, Value>): void {
    this.toggleLoading();
    HttpService.post<DeviceRegisterResponse>(Endpoint.DEVICES_REGISTER, undefined, { body })
      .then(() => {
        this.showToast('patient-evolution.release-evolution', ToastLevel.SUCCESS);
        applicationContext.popNavigationItem();
      })
      .catch(err => {
        this.toggleLoading();
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      })
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  private showModalSubmit(body: Record<string, Value>): void {
    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: 'text.error.device-amount-quantity',
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.registerDevice(body) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    }

    SimpleDialog.openDialog(dialogParams);
  }

  private handlerRegisterDevice(response: DeviceRegisterResponse, body: Record<string, Value>): void {
    this.toggleLoading()
    if (response.reasonExpression && response.reasonExpression === EXPRESSION_NOT_BE_INSTALL) {
      this.showToast('text.error.device-limit', ToastLevel.ERROR)
      return;
    }
    if (response.reasonExpression && response.reasonExpression === EXPRESSION_WANT_CONTINUE) {
      this.showModalSubmit(body);
      return;
    }
    this.registerDevice(body);
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      id: FragmentNames.DEVICES_CREATE,
      level,
      message: message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private validateAllFields(): void {
    const { topography,device } = this.devicesFormApi.getRecord() as unknown as FormData;

    device && this.context.set({ deviceSequence: device.id });
    this.sideInputApi.setEnabled(false);

    if(topography) {
      HttpService.get<ShowSideResponse>(Endpoint.GET_IS_SHOW_SIDE, { topographySequence: topography.topographySequence })
        .then(response => response.body && this.sideInputApi.setEnabled(response.body.showSide === 'S'))
    }

    this.caliberAutocompleteApi.setEnabled(!!device);
    this.topographyAutocompleteApi.setEnabled(!!device);
    this.actionAutocompleteApi.setEnabled(!!device);
    this.additionalAutocompleteApi.setEnabled(!!device);
  }

  private toggleLoading(): void {
    this.devicesFormApi.setLoading(!this.devicesFormApi.isLoading());
  }
}
